/**
 * DrawingCanvas — the takeoff work surface. Renders the PDF page (or image)
 * below a normalized SVG overlay (1000 units wide, unitH tall) and implements
 * every tool: select/move, box select, highlight, cut, scale, linear, area,
 * count — plus zoom/pan/pinch, keyboard shortcuts, hover tooltips, draft
 * readouts, and the addendum overlay blend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { openPdfDoc, renderPageToCanvas, dropPdf } from "./pdfSetup";
import { fetchDrawingUrl, fetchTiffPage, cadSnap, cadPick, cadEdit } from "./api";
import { toCadFrame, fromCadFrame, pxToUnits } from "./cadGeom";
import { isPdf, isImage, isTiff } from "./theme";
import {
  unitHeight, fmtFtIn, measureQuantity, hitTestItem, rectIntersectsItem,
  nearestOnPolyline, cutPolyline, snap45, groupBounds, translateItems,
  clampGroup, rescaleY, uid,
} from "./geometry";
import { polyLen, ductSqFt } from "./pricing";
import ViewerToolbar from "./ViewerToolbar";

const ZOOM_MIN = 25, ZOOM_MAX = 1000;
/** A press that travels less than this many screen px is a click (commits a
 * point); past it the gesture becomes a pan and commits nothing. ~5 CSS px
 * so it feels identical at 25% and 1000% zoom. */
const PAN_CLICK_PX = 5;

const hexRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return [37, 99, 235];
  const v = parseInt(m[1], 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};

/** Recolour a rendered page's ink to a single colour; overlay pages also
 * make white paper transparent. Returns a new canvas. */
const recolorInk = (src, hex, transparentWhite) => {
  const out = document.createElement("canvas");
  out.width = src.width; out.height = src.height;
  const ctx = out.getContext("2d");
  ctx.drawImage(src, 0, 0);
  const img = ctx.getImageData(0, 0, out.width, out.height);
  const d = img.data;
  const [r, g, b] = hexRgb(hex);
  for (let i = 0; i < d.length; i += 4) {
    const lum = (d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114) / 255;
    const ink = 1 - lum;
    if (transparentWhite) {
      d[i] = r; d[i + 1] = g; d[i + 2] = b;
      d[i + 3] = Math.round(ink * 255);
    } else {
      d[i] = Math.round(255 + (r - 255) * ink);
      d[i + 1] = Math.round(255 + (g - 255) * ink);
      d[i + 2] = Math.round(255 + (b - 255) * ink);
      d[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return out;
};

const isTypingTarget = (e) =>
  e.target?.closest?.("input, textarea, select, [contenteditable=true], button") != null;

// In-session page cache: returning to an already-rendered page is a bitmap
// blit instead of a full pdf.js re-render. LRU, capped so big scanned pages
// don't eat phone memory.
const pageBmpCache = new Map(); // key -> ImageBitmap
const BMP_CACHE_MAX = 16;
const bmpGet = (key) => {
  const b = pageBmpCache.get(key);
  if (b) { pageBmpCache.delete(key); pageBmpCache.set(key, b); } // refresh LRU
  return b;
};
const bmpSet = (key, bmp) => {
  if (!bmp) return;
  pageBmpCache.get(key)?.close?.();
  pageBmpCache.set(key, bmp);
  while (pageBmpCache.size > BMP_CACHE_MAX) {
    const [k, v] = pageBmpCache.entries().next().value;
    v.close?.();
    pageBmpCache.delete(k);
  }
};

const CAD_TOOLS = new Set(["cadline", "cadpoly", "cadtext", "cadmove", "caddelete", "cadoffset", "caddim", "cadsymbol"]);
/** Format a length in feet per the chosen dimension style. */
export const fmtDim = (ft, fmt) => {
  if (fmt === "m") return `${(ft / 3.280839895).toFixed(2)} m`;
  if (fmt === "mm") return `${Math.round(ft / 0.003280839895)} mm`;
  if (fmt === "ft") return `${ft.toFixed(2)}'`;
  const f = Math.floor(ft); let i = Math.round((ft - f) * 12); let F = f; if (i === 12) { F += 1; i = 0; }
  return `${F}'-${i}"`;
};
/** Grid snap + ortho lock relative to an anchor point (page units). */
const constrain = (pt, anchor, draw) => {
  let p = pt;
  if (draw?.ortho && anchor) p = Math.abs(p.x - anchor.x) >= Math.abs(p.y - anchor.y) ? { x: p.x, y: anchor.y } : { x: anchor.x, y: p.y };
  if (draw?.grid) p = { x: Math.round(p.x / draw.grid) * draw.grid, y: Math.round(p.y / draw.grid) * draw.grid };
  return p;
};

export default function DrawingCanvas({
  drawing, pageNum, onPage, pageState, items,
  tool, setTool, activeItem, snap, onToggleSnap,
  selection, setSelection,
  clipboard, setClipboard,
  mutate, autoNaming, autoNameScope, onAutoNameRegion, onScalePoints,
  onDims, overlay, onOpenGallery, actionBus, onUndo,
  sheetNav, cad = null, cadLayer = "0", cadDraw = null, onCadChange,
}) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const svgRef = useRef(null);
  const docRef = useRef(null);      // pdfjs doc
  const imgRef = useRef(null);      // HTMLImageElement for image drawings
  const renderSeq = useRef(0);
  const cursorRef = useRef(null);   // last pointer position in units
  const pasteCount = useRef(0);
  const pointers = useRef(new Map()); // active pointers (pinch)
  const pinchBase = useRef(null);

  const [numPages, setNumPages] = useState(1);
  const [docReady, setDocReady] = useState(0); // bumps when the pdf/image is loaded
  const [pageDims, setPageDims] = useState(null); // {w,h, paperIn} of rotated page
  const [container, setContainer] = useState({ w: 1200, h: 700 });
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [draft, setDraft] = useState(null);       // {type, points:[...]}
  const [cursor, setCursor] = useState(null);     // units, for draft preview
  const [scaleP1, setScaleP1] = useState(null);
  const [dragRect, setDragRect] = useState(null); // box select / highlight / autoname
  const [moveState, setMoveState] = useState(null); // {start, ids, orig: {id: points}, delta}
  const [vertexDrag, setVertexDrag] = useState(null); // {id, index, type, orig: point, cur: point}
  const [panState, setPanState] = useState(null);
  const lastPanEndRef = useRef(0); // suppress the synthetic click/dbl-click trailing a pan
  const [pendingClick, setPendingClick] = useState(null); // takeoff-tool press that may become a pan
  const [pendingPan, setPendingPan] = useState(null);     // mid-draft select press that may become a pan
  const [spaceHeld, setSpaceHeld] = useState(false);
  const [cursorClient, setCursorClient] = useState(null); // client px, for the live footage label
  const [hover, setHover] = useState(null);
  const [cadSnapMark, setCadSnapMark] = useState(null); // last CAD snap hit (flash)
  const [loadErr, setLoadErr] = useState(null);
  const [pageLoading, setPageLoading] = useState(true); // immediate feedback on page select
  const [retryTick, setRetryTick] = useState(0); // bump to re-run the load/render effects

  const pdf = isPdf(drawing);
  const img = isImage(drawing);
  const tif = isTiff(drawing);
  // TIFF pages are server-rendered images — two width buckets: fit vs deep zoom
  const tiffW = (pageState.zoom || 1) > 1.6 ? 6400 : 2600;
  const rotation = pageState.rotation || 0;
  const unitH = pageDims ? unitHeight(pageDims.w, pageDims.h) : 1000;

  // ---------- document load ----------
  // CAD drawings re-render their PDF when layers change: drop the cached parse
  const renderVersion = drawing?.cad?.render_version || 0;
  useEffect(() => { if (drawing?.cad) dropPdf(drawing.drawing_id); }, [drawing?.drawing_id, renderVersion]); // eslint-disable-line
  useEffect(() => {
    let alive = true;
    setLoadErr(null);
    setPageDims(null);
    setPageLoading(true); // selecting a drawing gives immediate feedback
    docRef.current = null;
    imgRef.current = null;
    (async () => {
      try {
        if (tif) return; // TIFF pages load in their own effect below
        if (pdf) {
          const doc = await openPdfDoc(drawing.drawing_id);
          if (!alive) return;
          docRef.current = doc;
          setNumPages(doc.numPages);
          setDocReady((n) => n + 1);
        } else if (img) {
          const url = await fetchDrawingUrl(drawing.drawing_id, drawing.mime || undefined);
          const im = new Image();
          await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = url; });
          if (!alive) return;
          // A truncated/incomplete upload can "load" with no pixels — that's
          // the thin-strip-and-spinner-forever photo bug. Surface it instead.
          if (!im.naturalWidth || !im.naturalHeight) throw new Error("decoded image has no pixels");
          imgRef.current = im;
          setNumPages(1);
          setDocReady((n) => n + 1);
        }
      } catch (e) {
        console.error("Takeoff drawing load failed:", e);
        if (alive) setLoadErr(img
          ? "This photo could not be opened — the file may be incomplete. Retry, or re-upload it."
          : "This drawing could not be loaded — try re-uploading it.");
      }
    })();
    // Watchdog: a photo/PDF that never arrives (connection saturated by an
    // upload, dead proxy) must not leave "Loading page…" on screen forever.
    const slow = setTimeout(() => { if (alive && !docRef.current && !imgRef.current) setLoadErr("Still waiting for this file — your connection may be busy uploading. Retry in a moment."); }, 60000);
    return () => { alive = false; clearTimeout(slow); };
  }, [drawing?.drawing_id, retryTick, renderVersion, pdf]); // eslint-disable-line

  // ---------- TIFF page load (PlanSwift-style converted drawings) ----------
  // Each page is a server-rendered, disk-cached web image — opening/flipping
  // is a plain image blit with zero PDF parsing. Reloads on page change and
  // when the zoom bucket (fit 2600 / deep 6400) crosses.
  useEffect(() => {
    if (!tif) return undefined;
    let alive = true;
    setLoadErr(null);
    setPageLoading(true);
    (async () => {
      try {
        const url = await fetchTiffPage(drawing.drawing_id, Math.min(pageNum, drawing.pages || 1), tiffW);
        const im = new Image();
        await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = url; });
        if (!alive) return;
        imgRef.current = im;
        setNumPages(drawing.pages || 1);
        setDocReady((n) => n + 1);
      } catch (e) {
        console.error("Takeoff TIFF page load failed:", e);
        if (alive) setLoadErr("This page could not be rendered — try re-uploading the file.");
      }
    })();
    return () => { alive = false; };
  }, [tif, drawing?.drawing_id, pageNum, tiffW, drawing?.pages]); // eslint-disable-line

  // ---------- page dims (+ report up) ----------
  useEffect(() => {
    let alive = true;
    (async () => {
      if (pdf && docRef.current) {
        const page = await docRef.current.getPage(Math.min(pageNum, docRef.current.numPages));
        const base = page.getViewport({ scale: 1 });
        const rot = (((base.rotation + rotation) % 360) + 360) % 360;
        const vp = page.getViewport({ scale: 1, rotation: rot });
        if (!alive) return;
        setPageDims({ w: vp.width, h: vp.height, paperIn: vp.width / 72 });
      } else if ((img || tif) && imgRef.current) {
        const im = imgRef.current;
        const rot90 = rotation % 180 !== 0;
        const w = rot90 ? im.naturalHeight : im.naturalWidth;
        const h = rot90 ? im.naturalWidth : im.naturalHeight;
        setPageDims({ w, h, paperIn: w / 96 });
      }
    })();
    return () => { alive = false; };
  }, [pdf, img, tif, pageNum, rotation, drawing?.drawing_id, docReady]);

  useEffect(() => {
    if (pageDims && onDims) onDims(unitHeight(pageDims.w, pageDims.h), pageDims.paperIn);
  }, [pageDims]); // eslint-disable-line

  // ---------- container size ----------
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setContainer({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setContainer({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  const baseW = Math.max(200, container.w - 40);
  const displayW = (baseW * zoom) / 100;
  const displayH = pageDims ? (displayW * pageDims.h) / pageDims.w : 0;
  const ppu = displayW / 1000; // screen px per drawing unit

  const fitPage = useCallback(() => {
    if (!pageDims) return;
    const zoomH = ((container.h - 40) / ((baseW * pageDims.h) / pageDims.w)) * 100;
    setZoom(Math.max(ZOOM_MIN, Math.min(100, zoomH)));
    setPan({ x: 0, y: 0 });
  }, [pageDims, container.h, baseW]);

  useEffect(() => { fitPage(); }, [drawing?.drawing_id, pageNum, rotation]); // eslint-disable-line

  // ---------- page render ----------
  useEffect(() => {
    if (!pageDims || !canvasRef.current) return undefined;
    const seq = ++renderSeq.current;
    // Only show the "Loading page…" indicator when a render takes a beat —
    // cache hits and fast pages never flash it.
    const showTimer = setTimeout(() => setPageLoading(true), 250);
    const timer = setTimeout(async () => {
      const dpr = Math.min(2.5, window.devicePixelRatio || 1);
      let targetW = Math.max(300, Math.round(displayW * dpr));
      const aspect = pageDims.h / pageDims.w;
      if (targetW > 10000) targetW = 10000;
      if (targetW * targetW * aspect > 70_000_000) targetW = Math.floor(Math.sqrt(70_000_000 / aspect));
      try {
        const cacheKey = `${drawing.drawing_id}|${pageNum}|${rotation}|${targetW}`;
        let srcCanvas = null;
        const cached = bmpGet(cacheKey);
        if (cached) {
          // revisit: blit the cached bitmap — no pdf.js work at all
          const c2 = document.createElement("canvas");
          c2.width = cached.width; c2.height = cached.height;
          c2.getContext("2d").drawImage(cached, 0, 0);
          srcCanvas = c2;
        } else if (pdf && docRef.current) {
          srcCanvas = (await renderPageToCanvas(docRef.current, Math.min(pageNum, numPages), rotation, targetW)).canvas;
          if (window.createImageBitmap) createImageBitmap(srcCanvas).then((b) => bmpSet(cacheKey, b)).catch(() => {});
        } else if ((img || tif) && imgRef.current) {
          const c = document.createElement("canvas");
          c.width = targetW; c.height = Math.round(targetW * aspect);
          const cx = c.getContext("2d");
          cx.save();
          cx.translate(c.width / 2, c.height / 2);
          cx.rotate((rotation * Math.PI) / 180);
          const iw = rotation % 180 ? c.height : c.width;
          const ih = rotation % 180 ? c.width : c.height;
          cx.drawImage(imgRef.current, -iw / 2, -ih / 2, iw, ih);
          cx.restore();
          srcCanvas = c;
          if (window.createImageBitmap) createImageBitmap(c).then((b) => bmpSet(cacheKey, b)).catch(() => {});
        }
        if (!srcCanvas || seq !== renderSeq.current) return;
        const el = canvasRef.current;
        el.width = srcCanvas.width; el.height = srcCanvas.height;
        const base = overlay ? recolorInk(srcCanvas, overlay.baseColor, false) : srcCanvas;
        el.getContext("2d").drawImage(base, 0, 0);
        clearTimeout(showTimer);
        setPageLoading(false);
        // addendum overlay page
        if (overlay && overlayCanvasRef.current && overlay.fileId) {
          const odoc = await openPdfDoc(overlay.fileId);
          const osrc = await renderPageToCanvas(odoc, overlay.page, overlay.rotation || 0, 1600);
          if (seq !== renderSeq.current) return;
          const recol = recolorInk(osrc.canvas, overlay.overlayColor, true);
          const oe = overlayCanvasRef.current;
          oe.width = recol.width; oe.height = recol.height;
          oe.getContext("2d").drawImage(recol, 0, 0);
        }
      } catch (e) {
        // Cancelled renders (user flipped page mid-render) throw too — those
        // have a stale seq. A real failure on the CURRENT render surfaces so
        // the user isn't staring at a spinner or a blank strip forever.
        if (seq === renderSeq.current) {
          clearTimeout(showTimer);
          setPageLoading(false);
          setLoadErr("This page could not be rendered — tap it again to retry.");
        }
      }
    }, 120);
    return () => { clearTimeout(showTimer); clearTimeout(timer); };
  }, [pageDims, displayW, pageNum, rotation, pdf, img, tif, numPages, overlay, retryTick]);

  // ---------- coordinate helpers ----------
  const toUnits = useCallback((e) => {
    const r = svgRef.current?.getBoundingClientRect();
    if (!r || !r.width) return null;
    return {
      x: Math.max(0, Math.min(1000, ((e.clientX - r.left) / r.width) * 1000)),
      y: Math.max(0, Math.min(unitH, ((e.clientY - r.top) / r.height) * unitH)),
    };
  }, [unitH]);

  const zoomAtPointer = useCallback((clientX, clientY, newZoomRaw) => {
    const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, newZoomRaw));
    const rect = svgRef.current?.getBoundingClientRect();
    const crect = containerRef.current?.getBoundingClientRect();
    if (!rect || !crect || !pageDims) { setZoom(newZoom); return; }
    const fx = (clientX - rect.left) / rect.width;
    const fy = (clientY - rect.top) / rect.height;
    const W2 = (baseW * newZoom) / 100;
    const H2 = (W2 * pageDims.h) / pageDims.w;
    const rx = clientX - crect.left, ry = clientY - crect.top;
    setPan({ x: rx - fx * W2 - (crect.width - W2) / 2, y: ry - fy * H2 - (crect.height - H2) / 2 });
    setZoom(newZoom);
  }, [baseW, pageDims]);

  const onWheel = useCallback((e) => {
    if (!pageDims) return;
    e.preventDefault();
    zoomAtPointer(e.clientX, e.clientY, zoom + (e.deltaY < 0 ? 25 : -25));
  }, [zoom, pageDims, zoomAtPointer]);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const h = (e) => onWheel(e);
    el.addEventListener("wheel", h, { passive: false });
    return () => el.removeEventListener("wheel", h);
  }, [onWheel]);

  // ---------- commit helpers ----------
  const fpu = pageState.feetPerUnit || 0;

  const commitItem = useCallback((type, points, labels = []) => {
    if (!activeItem) {
      toast.message("Pick a job item in the inspector first, then start measuring.");
      return;
    }
    const item = {
      id: uid(), fileId: drawing.drawing_id, page: pageNum, type,
      tagId: activeItem.item_id, name: activeItem.name, color: activeItem.color,
      lineWidth: 4, points,
      quantity: measureQuantity(type, points, fpu),
      unit: activeItem.unit, rate: activeItem.rate, labels: [...labels], multiplier: 1,
      category: activeItem.category,
      ...(activeItem.priceTo ? { priceTo: activeItem.priceTo } : {}),
      ...(activeItem.ductSpec ? { ductSpec: activeItem.ductSpec } : {}),
    };
    mutate((d) => { d.items.push(item); });
    // spec: finishing a line must NOT select it — no orange outline, no
    // handles, no edit state. It becomes selectable only via the Select
    // tool; the active item stays armed so the next line starts at once.
  }, [activeItem, drawing, pageNum, fpu, mutate, setSelection]);

  // ---------- CAD-exact snap / pick (DWG drawings only) ----------
  const snapCadPoint = useCallback(async (p) => {
    try {
      const r = pxToUnits(10, svgRef.current);
      const [q] = toCadFrame([p], rotation, unitH);
      const hit = await cadSnap(drawing.drawing_id, pageNum, q.x, q.y, r);
      if (!hit) return p;
      const [back] = fromCadFrame([{ x: hit.x, y: hit.y }], rotation, unitH);
      setCadSnapMark({ ...back, kind: hit.kind });
      setTimeout(() => setCadSnapMark(null), 900);
      return back;
    } catch { return p; }
  }, [drawing, pageNum, rotation, unitH]);

  const pickCadEntity = useCallback(async (p) => {
    try {
      const r = pxToUnits(8, svgRef.current);
      const [q] = toCadFrame([p], rotation, unitH);
      const ent = await cadPick(drawing.drawing_id, pageNum, q.x, q.y, r);
      if (!ent) { toast.message("No CAD line under the cursor — zoom in or click closer to it."); return; }
      const pts = fromCadFrame(ent.points.map(([x, y]) => ({ x, y })), rotation, unitH);
      if (ent.type === "BLOCK" || pts.length < 2) {
        // one click counts every matching block (same name + layer) on the page
        const matches = ent.matches?.length ? fromCadFrame(ent.matches.map(([x, y]) => ({ x, y })), rotation, unitH) : [p];
        commitItem("count", matches, ent.block ? [`${ent.block.name}`, ent.block.layer] : []);
        toast.success(ent.block ? `Counted ${matches.length} × ${ent.block.name} on ${ent.block.layer}` : "Counted 1");
        return;
      }
      const type = ent.closed && pts.length >= 3 ? "area" : "linear";
      const tags = [];
      if (ent.pipe?.summary) tags.push(ent.pipe.summary);
      commitItem(type, pts, tags);
      const ft = ent.length_ft;
      toast.success(`${ent.type} on ${ent.layer}${ft ? ` — ${ft.toLocaleString(undefined, { maximumFractionDigits: 1 })} ft (CAD exact)` : ""}${ent.pipe?.summary ? ` · ${ent.pipe.summary}` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "CAD pick failed"); }
  }, [drawing, pageNum, rotation, unitH, commitItem]);

  // ---------- CAD drawing tools (saved server-side as edits -> DXF copy) ----------
  const [cadSel, setCadSel] = useState(null); // picked entity awaiting a 2nd click (move / offset)
  const submitCadEdit = useCallback(async (edit) => {
    try {
      const toCad = (pts) => toCadFrame(pts, rotation, unitH).map((q) => [q.x, q.y]);
      const body = { ...edit, page: pageNum, layer: cadLayer || "0" };
      if (edit.pts) body.pts = toCad(edit.pts.map(([x, y]) => ({ x, y })));
      if (edit.at) body.at = toCad([{ x: edit.at[0], y: edit.at[1] }])[0];
      if (edit.op === "move") {
        const [a, b] = toCad([{ x: 0, y: 0 }, { x: edit.dx, y: edit.dy }]);
        body.dx = b[0] - a[0]; body.dy = b[1] - a[1];
      }
      const r = await cadEdit(drawing.drawing_id, body);
      onCadChange?.((cur) => cur ? { ...cur, status: "rendering", edits: { ...(cur.edits || {}), [String(pageNum)]: [...((cur.edits || {})[String(pageNum)] || []), r.edit] } } : cur);
      toast.success({ add_line: "Line added", add_polyline: "Polyline added", add_text: "Text added", add_dim: "Dimension added", add_symbol: "Symbol stamped", move: "Entity moved", delete: "Entity deleted", offset: "Offset copy added" }[edit.op] + " — redrawing the sheet…");
    } catch (e) { toast.error(e?.response?.data?.detail || "CAD edit failed"); }
  }, [drawing, pageNum, rotation, unitH, cadLayer, onCadChange]);

  const cadToolClick = useCallback(async (p) => {
    const anchor = draft?.cad ? draft.points[draft.points.length - 1] : cadSel?.dim || null;
    const pt = constrain(snap && !cadDraw?.grid ? await snapCadPoint(p) : p, anchor, cadDraw);
    if (tool === "cadsymbol") { submitCadEdit({ op: "add_symbol", pts: [[pt.x, pt.y]], symbol: cadDraw?.symbol || "manhole", height: cadDraw?.dimHeight || 2.5 }); return; }
    if (tool === "cadline") {
      if (!draft || draft.cad !== "line") { setDraft({ type: "linear", cad: "line", points: [pt] }); return; }
      submitCadEdit({ op: "add_line", pts: [[draft.points[0].x, draft.points[0].y], [pt.x, pt.y]] });
      setDraft(null);
      return;
    }
    if (tool === "cadpoly") {
      setDraft((cur) => (cur && cur.cad === "poly" ? { ...cur, points: [...cur.points, pt] } : { type: "linear", cad: "poly", points: [pt] }));
      return;
    }
    if (tool === "cadtext") {
      const text = window.prompt("Text to place on the drawing:");
      if (text && text.trim()) submitCadEdit({ op: "add_text", pts: [[pt.x, pt.y]], text: text.trim(), height: 3 });
      return;
    }
    if (tool === "caddim") {
      if (!cadSel || cadSel.dim == null) { setCadSel({ dim: pt }); toast.message("Now click the second point of the dimension."); return; }
      submitCadEdit({ op: "add_dim", pts: [[cadSel.dim.x, cadSel.dim.y], [pt.x, pt.y]], format: cadDraw?.dimFormat || "ftin", height: cadDraw?.dimHeight || 2.5 });
      setCadSel(null);
      return;
    }
    // move / delete / offset work on a picked entity
    if (!cadSel) {
      try {
        const r = pxToUnits(8, svgRef.current);
        const [q] = toCadFrame([p], rotation, unitH);
        const ent = await cadPick(drawing.drawing_id, pageNum, q.x, q.y, r);
        if (!ent || !ent.handle) { toast.message(ent ? "Blocks can't be edited yet — pick a line, polyline or arc." : "No CAD entity under the cursor."); return; }
        const pts = fromCadFrame(ent.points.map(([x, y]) => ({ x, y })), rotation, unitH);
        if (tool === "caddelete") { submitCadEdit({ op: "delete", handle: ent.handle }); return; }
        setCadSel({ ent, pts, at: p });
        toast.message(tool === "cadmove" ? "Now click where it should go." : "Now click on the side to offset to.");
      } catch (e) { toast.error(e?.response?.data?.detail || "CAD pick failed"); }
      return;
    }
    if (tool === "cadmove") submitCadEdit({ op: "move", handle: cadSel.ent.handle, dx: pt.x - cadSel.at.x, dy: pt.y - cadSel.at.y });
    if (tool === "cadoffset") {
      const near = nearestOnPolyline(cadSel.pts, pt);
      const dist = near ? near.dist : 0;
      if (dist < 0.5) { toast.message("Click a little further from the line to set the offset distance."); return; }
      submitCadEdit({ op: "offset", handle: cadSel.ent.handle, distance: dist, at: [pt.x, pt.y] });
    }
    setCadSel(null);
  }, [tool, draft, cadSel, snap, snapCadPoint, submitCadEdit, drawing, pageNum, rotation, unitH, cadDraw]);
  useEffect(() => { setCadSel(null); if (draft?.cad) setDraft(null); }, [tool]); // eslint-disable-line

  // CAD edits not yet in the rendered PDF (preview until the sheet redraws)
  const cadFpu = useMemo(() => (cad?.pages?.find((p) => p.page === pageNum)?.feet_per_unit) || 0, [cad, pageNum]);
  const pendingEdits = useMemo(() => {
    if (!cad) return [];
    const list = (cad.edits || {})[String(pageNum)] || [];
    const renderedAt = drawing?.cad?.rendered_at ? Date.parse(drawing.cad.rendered_at) : 0;
    return list.filter((e) => e.pts && (e.at_ms || 0) > renderedAt).map((e) => ({ ...e, pts: fromCadFrame(e.pts.map(([x, y]) => ({ x, y })), rotation, unitH) }));
  }, [cad, pageNum, drawing?.cad?.rendered_at, rotation, unitH]);

  const finishDraft = useCallback(() => {
    // NOTE: never commit inside a setState updater — StrictMode double-invokes
    // updaters and the takeoff would be created twice.
    if (!draft) return;
    if (draft.cad) { // CAD polyline being drawn -> saved as a CAD edit
      if (draft.points.length >= 2) submitCadEdit({ op: "add_polyline", pts: draft.points.map((p) => [p.x, p.y]), closed: false });
      setDraft(null);
      return;
    }
    const min = draft.type === "linear" ? 2 : 3;
    if (draft.points.length >= min) commitItem(draft.type, draft.points);
    setDraft(null);
  }, [draft, commitItem, submitCadEdit]);

  const undoPoint = useCallback(() => {
    setDraft((cur) => (cur && cur.points.length ? { ...cur, points: cur.points.slice(0, -1) } : cur));
  }, []);

  const deleteSelected = useCallback(() => {
    if (!selection.length) return;
    mutate((d) => { d.items = d.items.filter((i) => !selection.includes(i.id)); });
    setSelection([]);
  }, [selection, mutate, setSelection]);

  const copySelection = useCallback(() => {
    const picked = items.filter((i) => selection.includes(i.id));
    if (!picked.length) return;
    setClipboard({ items: structuredClone(picked), unitH });
    toast.success(`${picked.length} takeoff${picked.length > 1 ? "s" : ""} copied`);
  }, [items, selection, setClipboard, unitH]);

  const pasteClipboard = useCallback(() => {
    if (!clipboard?.items?.length) return;
    let pasted = structuredClone(clipboard.items);
    pasted = rescaleY(pasted, clipboard.unitH, unitH);
    const b = groupBounds(pasted);
    if (cursorRef.current && b) {
      pasted = translateItems(pasted, cursorRef.current.x - b.cx, cursorRef.current.y - b.cy);
    } else {
      pasteCount.current += 1;
      pasted = translateItems(pasted, 12 * pasteCount.current, 12 * pasteCount.current);
    }
    pasted = clampGroup(pasted, unitH);
    const ids = pasted.map(() => uid());
    pasted = pasted.map((it, i) => ({
      ...it, id: ids[i], fileId: drawing.drawing_id, page: pageNum,
      quantity: measureQuantity(it.type, it.points, fpu),
    }));
    mutate((d) => { d.items.push(...pasted); });
    setSelection(ids);
    toast.success(`Pasted ${pasted.length} takeoff${pasted.length > 1 ? "s" : ""}`);
  }, [clipboard, unitH, drawing, pageNum, fpu, mutate, setSelection]);

  const selectAll = useCallback(() => setSelection(items.map((i) => i.id)), [items, setSelection]);

  // expose rail actions (copy / paste / undo point / delete selected)
  useEffect(() => {
    if (!actionBus) return undefined;
    actionBus.current = { copy: copySelection, paste: pasteClipboard, undo: undoPoint, deleteSel: deleteSelected };
    return () => { if (actionBus) actionBus.current = null; };
  }, [actionBus, copySelection, pasteClipboard, undoPoint, deleteSelected]);

  // ---------- pointer handling ----------
  const onPointerDown = (e) => {
    // Viewer toolbar / any interactive child must keep its clicks — capturing
    // the pointer here would retarget the click to the canvas container and
    // swallow it (and takeoff tools would draw a point under the button).
    if (e.target.closest?.("button, input, a, select, textarea")) return;
    const middlePan = e.button === 1 && e.pointerType === "mouse";
    if (e.button !== 0 && !middlePan && e.pointerType === "mouse") return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchBase.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom, mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
      setDraft(null); setDragRect(null); setMoveState(null); setPanState(null);
      setPendingClick(null); setPendingPan(null); setVertexDrag(null);
      return;
    }
    // middle-mouse or Space-drag always pans, whatever tool is active
    if (middlePan || spaceHeld) {
      setPendingClick(null); setPendingPan(null);
      setPanState({ startX: e.clientX, startY: e.clientY, pan0: pan });
      return;
    }
    const p = toUnits(e);
    if (!p) return;
    cursorRef.current = p;
    setCursorClient({ x: e.clientX, y: e.clientY });

    if (autoNaming) { setDragRect({ x: p.x, y: p.y, w: 0, h: 0, kind: "autoname" }); return; }
    switch (tool) {
      case "select": {
        if (draft) {
          // mid-draft: a drag pans the sheet — it must not grab or move the
          // takeoffs underneath the pointer.
          setPendingPan({ sx: e.clientX, sy: e.clientY, pan0: pan });
          break;
        }
        const tol = 14 / (ppu || 1);
        // vertex handles win over item moves: a single selected linear/area
        // takeoff exposes a draggable handle at every vertex
        if (vertexItem) {
          const vtol = 12 / (ppu || 1);
          const vidx = vertexItem.points.findIndex((pt) => Math.hypot(pt.x - p.x, pt.y - p.y) <= vtol);
          if (vidx >= 0) {
            setVertexDrag({ id: vertexItem.id, index: vidx, type: vertexItem.type, orig: { ...vertexItem.points[vidx] }, cur: { ...vertexItem.points[vidx] }, moved: false });
            break;
          }
        }
        const hit = [...items].reverse().find((it) => hitTestItem(it, p, tol));
        if (hit) {
          let ids = selection;
          if (e.shiftKey) {
            ids = selection.includes(hit.id) ? selection.filter((i) => i !== hit.id) : [...selection, hit.id];
            setSelection(ids);
          } else if (!selection.includes(hit.id)) {
            ids = [hit.id];
            setSelection(ids);
          }
          const orig = {};
          for (const it of items) if (ids.includes(it.id)) orig[it.id] = structuredClone(it.points);
          setMoveState({ start: p, ids, orig, delta: { x: 0, y: 0 }, moved: false });
        } else {
          // pressing anywhere inside the multi-selection bounds drags the
          // whole group — no need to aim at a line
          const selItems = items.filter((i) => selection.includes(i.id));
          const gb = selItems.length > 1 ? groupBounds(selItems) : null;
          const pad = 10 / (ppu || 1);
          if (gb && p.x >= gb.minX - pad && p.x <= gb.maxX + pad && p.y >= gb.minY - pad && p.y <= gb.maxY + pad) {
            const orig = {};
            for (const it of items) if (selection.includes(it.id)) orig[it.id] = structuredClone(it.points);
            setMoveState({ start: p, ids: selection, orig, delta: { x: 0, y: 0 }, moved: false });
          } else {
            setPanState({ startX: e.clientX, startY: e.clientY, pan0: pan });
            if (!e.shiftKey) setSelection([]);
          }
        }
        break;
      }
      case "box": setDragRect({ x: p.x, y: p.y, w: 0, h: 0, kind: "box", shift: e.shiftKey }); break;
      case "highlight": setDragRect({ x: p.x, y: p.y, w: 0, h: 0, kind: "highlight" }); break;
      case "scale":
        if (!scaleP1) setScaleP1(p);
        else { onScalePoints(scaleP1, p); setScaleP1(null); }
        break;
      case "cut": {
        const tol = 14 / (ppu || 1);
        const linears = items.filter((i) => i.type === "linear");
        let best = null, bestD = Infinity;
        for (const it of linears) {
          const n = nearestOnPolyline(it.points, p);
          if (n.dist < bestD) { bestD = n.dist; best = { it, p: n.point }; }
        }
        if (!best || bestD > tol) { toast.message("Click directly on a linear takeoff to cut it."); break; }
        const parts = cutPolyline(best.it.points, best.p, Math.max(2, 8 / (ppu || 1)));
        if (!parts) { toast.message("Too close to an endpoint to cut."); break; }
        mutate((d) => {
          const idx = d.items.findIndex((i) => i.id === best.it.id);
          if (idx < 0) return;
          const mk = (pts) => ({
            ...best.it, id: uid(), points: pts,
            labels: [...(best.it.labels || [])],
            quantity: measureQuantity("linear", pts, fpu),
            ...(best.it.ductSpec ? { ductSpec: { ...best.it.ductSpec } } : {}),
          });
          d.items.splice(idx, 1, mk(parts[0]), mk(parts[1]));
        });
        toast.success("Takeoff cut in two — labels, multiplier and total length kept.");
        break;
      }
      case "cadpick": {
        if (!activeItem) { toast.message("Pick a job item in the inspector first, then click a CAD line or outline."); break; }
        setPendingClick({ pointerId: e.pointerId, sx: e.clientX, sy: e.clientY });
        break;
      }
      case "cadline": case "cadpoly": case "cadtext": case "cadmove": case "caddelete": case "cadoffset": case "caddim": case "cadsymbol": {
        if (!cad) break;
        setPendingClick({ pointerId: e.pointerId, sx: e.clientX, sy: e.clientY });
        break;
      }
      case "linear":
      case "area":
      case "count": {
        if (!activeItem) { toast.message(tool === "count" ? "Pick a job item in the inspector first, then start counting." : "Pick a job item in the inspector first, then start measuring."); break; }
        if (tool === "linear" && activeItem.category === "Pipe" && !fpu) {
          toast.error("Set this page's scale before starting a pipe takeoff.", {
            id: "pipe-needs-scale",
            action: { label: "Set scale", onClick: () => window.dispatchEvent(new CustomEvent("takeoff:focus-scale")) },
          });
          break;
        }
        // click-vs-drag: the point commits on release only if the press never
        // travelled far enough to become a pan.
        setPendingClick({ pointerId: e.pointerId, sx: e.clientX, sy: e.clientY });
        break;
      }
      default: break;
    }
  };

  const onPointerMove = (e) => {
    if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinchBase.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const { dist: d0, zoom: z0, mid } = pinchBase.current;
      if (d0 > 0) zoomAtPointer(mid.x, mid.y, (z0 * dist) / d0);
      return;
    }
    const p = toUnits(e);
    if (p) cursorRef.current = p;
    setCursorClient({ x: e.clientX, y: e.clientY });
    // a takeoff-tool press that travels becomes a pan and commits no point
    if (pendingClick && e.pointerId === pendingClick.pointerId) {
      if (Math.hypot(e.clientX - pendingClick.sx, e.clientY - pendingClick.sy) > PAN_CLICK_PX) {
        setPanState({ startX: pendingClick.sx, startY: pendingClick.sy, pan0: pan });
        setPendingClick(null);
      }
    }
    // a mid-draft select press likewise becomes a pan
    if (pendingPan && Math.hypot(e.clientX - pendingPan.sx, e.clientY - pendingPan.sy) > PAN_CLICK_PX) {
      setPanState({ startX: pendingPan.sx, startY: pendingPan.sy, pan0: pendingPan.pan0 });
      setPendingPan(null);
    }
    if (panState) {
      setPan({ x: panState.pan0.x + (e.clientX - panState.startX), y: panState.pan0.y + (e.clientY - panState.startY) });
      return;
    }
    if (!p) return;
    if (vertexDrag) {
      let np = p;
      // 45° snap anchors to the previous vertex when editing a line
      if (snap && vertexDrag.type === "linear" && vertexDrag.index > 0) {
        const anchor = items.find((i) => i.id === vertexDrag.id)?.points?.[vertexDrag.index - 1];
        if (anchor) np = snap45(anchor, p);
      }
      const moved = vertexDrag.moved || Math.hypot(np.x - vertexDrag.orig.x, np.y - vertexDrag.orig.y) > 0.5;
      setVertexDrag({ ...vertexDrag, cur: np, moved });
      return;
    }
    setCursor(p);
    if (dragRect) { setDragRect({ ...dragRect, w: p.x - dragRect.x, h: p.y - dragRect.y }); return; }
    if (moveState) {
      const delta = { x: p.x - moveState.start.x, y: p.y - moveState.start.y };
      if (Math.abs(delta.x) + Math.abs(delta.y) > 0.5) setMoveState({ ...moveState, delta, moved: true });
      return;
    }
    if (tool === "select" && !vertexDrag) {
      const tol = 14 / (ppu || 1);
      const hit = [...items].reverse().find((it) => hitTestItem(it, p, tol));
      setHover(hit ? { item: hit, cx: e.clientX, cy: e.clientY } : null);
    } else if (hover) setHover(null);
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchBase.current = null;
    if (pendingClick && e.pointerId === pendingClick.pointerId) {
      setPendingClick(null);
      const p2 = toUnits(e);
      if (p2 && cad && tool === "cadpick" && activeItem) { pickCadEntity(p2); return; }
      if (p2 && cad && CAD_TOOLS.has(tool)) { cadToolClick(p2); return; }
      if (p2 && activeItem && (tool === "linear" || tool === "area" || tool === "count")) {
        const place = (pt) => {
          if (tool === "count") {
            commitItem("count", [pt]);
          } else {
            // pure updater — safe under StrictMode double-invoke
            setDraft((cur) => {
              const base = cur && cur.type === tool ? cur : { type: tool, points: [] };
              let np = pt;
              if (tool === "linear" && snap && !cad && base.points.length) np = snap45(base.points[base.points.length - 1], pt);
              return { ...base, points: [...base.points, np] };
            });
          }
        };
        // CAD drawings: snap to the real endpoint / midpoint / line under the cursor
        if (cad && snap) snapCadPoint(p2).then(place); else place(p2);
      }
      return;
    }
    if (pendingPan) { setPendingPan(null); return; }
    if (panState) { lastPanEndRef.current = Date.now(); setPanState(null); return; }
    if (dragRect) {
      const r = normRect(dragRect);
      if (r.w > 2 && r.h > 2) {
        if (dragRect.kind === "box") {
          const hit = items.filter((it) => rectIntersectsItem(it, r)).map((i) => i.id);
          setSelection(dragRect.shift ? [...new Set([...selection, ...hit])] : hit);
        } else if (dragRect.kind === "highlight") {
          mutate((d) => {
            const key = `${drawing.drawing_id}:${pageNum}`;
            const pg = d.pages[key] || (d.pages[key] = { rotation: 0, highlights: [] });
            if (!pg.highlights) pg.highlights = [];
            pg.highlights.push({ id: uid(), ...r });
          });
        } else if (dragRect.kind === "autoname") {
          onAutoNameRegion?.(r);
        }
      }
      setDragRect(null);
      return;
    }
    if (moveState) {
      if (moveState.moved) {
        const { ids, delta } = moveState;
        mutate((d) => {
          for (const it of d.items) {
            if (!ids.includes(it.id)) continue;
            it.points = it.points.map((p) => ({ x: p.x + delta.x, y: p.y + delta.y }));
          }
        });
      }
      setMoveState(null);
    }
    if (vertexDrag) {
      // commit goes through mutate so the edit lands on the undo stack
      // (Ctrl+Z restores the pre-drag vertex) and autosaves
      if (vertexDrag.moved) {
        const { id, index, cur } = vertexDrag;
        mutate((d) => {
          const it = d.items.find((i) => i.id === id);
          if (!it || !it.points[index]) return;
          it.points = it.points.map((pt, i) => (i === index ? { x: cur.x, y: cur.y } : pt));
          it.quantity = measureQuantity(it.type, it.points, fpu);
        });
      }
      setVertexDrag(null);
    }
  };

  /** Lost/cancelled capture (OS gesture, touch interruption): drop every
   * in-flight gesture without committing — drafts survive untouched. */
  const onPointerCancel = (e) => {
    pointers.current.delete(e.pointerId);
    pinchBase.current = null;
    setPendingClick(null);
    setPendingPan(null);
    setPanState(null);
    setDragRect(null);
    setMoveState(null);
    setVertexDrag(null);
  };

  const onDblClick = () => {
    if (!draft) return;
    // a drag-pan still fires a synthetic click — two quick pans must not be
    // read as "finish the trace"
    if (Date.now() - lastPanEndRef.current < 500) return;
    // drop the duplicate point the finishing double-click just added
    if (draft.points.length >= 2) {
      const a = draft.points[draft.points.length - 2], b = draft.points[draft.points.length - 1];
      if (Math.hypot(a.x - b.x, a.y - b.y) < 2) setDraft((cur) => ({ ...cur, points: cur.points.slice(0, -1) }));
    }
    if (draft.cad) { // CAD polyline -> saved as a CAD edit (same path as Enter)
      const pts = draft.points.length >= 2 && Math.hypot(draft.points.at(-2).x - draft.points.at(-1).x, draft.points.at(-2).y - draft.points.at(-1).y) < 2
        ? draft.points.slice(0, -1) : draft.points;
      if (pts.length >= 2) submitCadEdit({ op: "add_polyline", pts: pts.map((p) => [p.x, p.y]), closed: false });
      setDraft(null);
      return;
    }
    let pts = draft.points;
    if (pts.length >= 2) {
      const a = pts[pts.length - 2], b = pts[pts.length - 1];
      if (Math.hypot(a.x - b.x, a.y - b.y) < 2) pts = pts.slice(0, -1);
    }
    const min = draft.type === "linear" ? 2 : 3;
    if (pts.length >= min) commitItem(draft.type, pts);
    setDraft(null);
  };

  // ---------- keyboard ----------
  useEffect(() => {
    const h = (e) => {
      if (isTypingTarget(e) || e.isComposing) return;
      const k = e.key;
      if ((e.ctrlKey || e.metaKey) && k.toLowerCase() === "c") { copySelection(); e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && k.toLowerCase() === "v") { pasteClipboard(); e.preventDefault(); return; }
      if ((e.ctrlKey || e.metaKey) && k.toLowerCase() === "z") { onUndo?.(); e.preventDefault(); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      switch (k) {
        case "s": case "S": if (tool === "linear") onToggleSnap?.(); break;
        case "c": case "C": setTool("cut"); break;
        case "Enter": finishDraft(); break;
        case "Backspace": if (draft) { undoPoint(); e.preventDefault(); } break;
        case "Delete": deleteSelected(); break;
        case "Escape":
          if (vertexDrag) setVertexDrag(null);
          else if (moveState) setMoveState(null);
          else if (draft) setDraft(null);
          else if (scaleP1) setScaleP1(null);
          else if (selection.length) setSelection([]);
          setTool("select");
          break;
        default: break;
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [tool, draft, selection, moveState, vertexDrag, scaleP1, copySelection, pasteClipboard, finishDraft, undoPoint, deleteSelected, setTool, setSelection, onToggleSnap, onUndo]);

  // hold Space to pan with any tool active
  useEffect(() => {
    const down = (e) => {
      if (isTypingTarget(e) || e.isComposing) return;
      if (e.key === " ") { setSpaceHeld(true); e.preventDefault(); }
    };
    const up = (e) => { if (e.key === " ") setSpaceHeld(false); };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  // ---------- render helpers ----------
  const movedIds = moveState?.moved ? moveState.ids : [];
  const renderItems = useMemo(() => items.map((it) => {
    if (movedIds.includes(it.id)) {
      return { ...it, points: translateItems([it], moveState.delta.x, moveState.delta.y)[0].points };
    }
    if (vertexDrag && it.id === vertexDrag.id) {
      return { ...it, points: it.points.map((p, i) => (i === vertexDrag.index ? vertexDrag.cur : p)) };
    }
    return it;
  }), [items, moveState, vertexDrag]); // eslint-disable-line

  const selBounds = useMemo(() => groupBounds(renderItems.filter((i) => selection.includes(i.id))), [renderItems, selection]);

  // running tally of count takeoffs on this page, grouped by block/label name
  const countLegend = useMemo(() => {
    const acc = new Map();
    for (const it of renderItems) {
      if (it.type !== "count") continue;
      const label = (it.labels && it.labels[0]) || it.name || "Count";
      const key = label.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "count";
      const row = acc.get(key) || { key, label, color: it.color || "#f97316", n: 0 };
      row.n += Math.max(1, (it.points || []).length);
      acc.set(key, row);
    }
    return [...acc.values()].sort((a, b) => b.n - a.n);
  }, [renderItems]);

  /** Vertex editing is offered when exactly one linear/area takeoff is
   * selected — every vertex gets a draggable handle. */
  const vertexItem = useMemo(() => {
    if (selection.length !== 1) return null;
    const it = renderItems.find((i) => i.id === selection[0]);
    return it && (it.type === "linear" || it.type === "area") ? it : null;
  }, [selection, renderItems]);

  /** Live quantity while a vertex is being dragged (feet for lines, sq ft
   * for areas) — shown in the cursor readout. */
  const vertexReadout = useMemo(() => {
    if (!vertexDrag) return null;
    const it = items.find((i) => i.id === vertexDrag.id);
    if (!it) return null;
    const pts = it.points.map((p, i) => (i === vertexDrag.index ? vertexDrag.cur : p));
    return { type: it.type, qty: measureQuantity(it.type, pts, fpu) };
  }, [vertexDrag, items, fpu]);

  /** The cursor position actually used for previews/readouts — snapped to
   * 45° when snap is on, so what you see is exactly what commits. */
  const previewCursor = useMemo(() => {
    if (!cursor) return null;
    if (draft && draft.type === "linear" && snap && draft.points.length) {
      return snap45(draft.points[draft.points.length - 1], cursor);
    }
    return cursor;
  }, [cursor, draft, snap]);

  const draftReadout = useMemo(() => {
    if (!draft || draft.type !== "linear" || !draft.points.length || !previewCursor) return null;
    const done = polyLen(draft.points) * fpu;
    const seg = Math.hypot(previewCursor.x - draft.points[draft.points.length - 1].x, previewCursor.y - draft.points[draft.points.length - 1].y) * fpu;
    return { done, seg, total: done + seg };
  }, [draft, previewCursor, fpu]);

  const cursorStyle = panState ? "grabbing"
    : spaceHeld ? "grab"
    : tool === "select" ? (vertexDrag ? "move" : moveState ? "grabbing" : "default")
    : "crosshair";

  if (loadErr) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="rounded-xl border border-red-200 bg-white px-6 py-4 text-center shadow">
          <p className="text-sm text-red-600" data-testid="canvas-load-error">{loadErr}</p>
          <button
            onClick={() => { setLoadErr(null); setPageLoading(true); setRetryTick((n) => n + 1); }}
            data-testid="canvas-retry-btn"
            className="mt-3 rounded-md border border-gray-300 px-4 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative flex-1 touch-none select-none overflow-hidden"
      style={{ background: "radial-gradient(1200px 800px at 50% 30%, #cbd4df, #aeb9c7)", cursor: cursorStyle }}
      data-testid="takeoff-canvas"
      onDragStart={(e) => e.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onDoubleClick={onDblClick}
    >
      <ViewerToolbar
        zoom={zoom}
        onZoom={(z) => setZoom(Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, z)))}
        onFit={fitPage}
        onOpenGallery={onOpenGallery}
        isPdf={pdf}
        isTiff={tif}
        numPages={numPages}
        pageNum={pageNum}
        onPage={onPage}
        bookmarked={!!pageState.bookmarked}
        onToggleBookmark={() => mutate((d) => {
          const key = `${drawing.drawing_id}:${pageNum}`;
          const pg = d.pages[key] || (d.pages[key] = { rotation: 0, highlights: [] });
          pg.bookmarked = !pg.bookmarked;
        })}
        scaleLabel={pageState.scaleLabel}
        snap={snap}
        snapVisible={tool === "linear" || (!!cad && (tool === "area" || tool === "count"))}
        onToggleSnap={onToggleSnap}
        sheetNav={sheetNav}
      />

      {pageLoading && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" data-testid="canvas-loading">
          <div className="flex items-center gap-2 rounded-full bg-black/60 px-4 py-2 text-xs font-semibold text-white shadow-lg">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            Loading page…
          </div>
        </div>
      )}

      {countLegend.length > 0 && (
        <div className="absolute bottom-4 left-4 z-20 max-w-[16rem] rounded-lg border border-slate-300 bg-white/95 shadow-lg backdrop-blur" data-testid="count-legend">
          <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-1.5">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">Count legend</span>
            <span className="text-[10px] font-semibold text-slate-400">{countLegend.reduce((s, r) => s + r.n, 0)} total</span>
          </div>
          <ul className="max-h-48 overflow-y-auto py-1">
            {countLegend.map((r) => (
              <li key={r.key} className="flex items-center gap-2 px-3 py-1 text-[11px]" data-testid={`count-legend-row-${r.key}`}>
                <span className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/20" style={{ background: r.color }} />
                <span className="min-w-0 flex-1 truncate text-slate-700" title={r.label}>{r.label}</span>
                <span className="shrink-0 font-bold tabular-nums text-slate-900">{r.n}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {autoNaming && (
        <div className="absolute left-1/2 top-16 z-10 -translate-x-1/2 rounded-full bg-[#f97316] px-4 py-1.5 text-xs font-semibold text-white shadow-lg" data-testid="autoname-instruction">
          {autoNameScope === "folder"
            ? "Drag a box tightly around the sheet number. The same spot is read on every page of every PDF in this folder. (Esc to cancel)"
            : "Drag a box tightly around the sheet number. The same location will be read on every page. (Esc to cancel)"}
        </div>
      )}

      {/* the page */}
      <div
        className="absolute left-1/2 top-1/2"
        style={{ transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px))`, width: displayW, height: displayH || 10 }}
      >
        <canvas ref={canvasRef} className="block h-full w-full rounded-sm shadow-2xl" style={{ background: "#fff" }} draggable={false} />
        {overlay && (
          <canvas
            ref={overlayCanvasRef}
            className="pointer-events-none absolute inset-0 h-full w-full"
            style={{ mixBlendMode: "multiply", opacity: (overlay.opacity ?? 60) / 100 }}
            data-testid="overlay-canvas"
          />
        )}
        <svg
          ref={svgRef}
          className="absolute inset-0 h-full w-full"
          viewBox={`0 0 1000 ${unitH}`}
          preserveAspectRatio="none"
          data-testid="takeoff-svg"
        >
          {/* highlights */}
          {(pageState.highlights || []).map((hgl) => (
            <rect key={hgl.id} x={hgl.x} y={hgl.y} width={hgl.w} height={hgl.h} fill="rgba(250,204,21,0.3)" />
          ))}
          {/* saved takeoffs */}
          {renderItems.map((it) => (
            <Mark key={it.id} it={it} ppu={ppu} selected={selection.includes(it.id)} hovered={hover?.item?.id === it.id} />
          ))}
          {cad && cadDraw?.grid > 0 && CAD_TOOLS.has(tool) && (
            <g data-testid="cad-grid-overlay" pointerEvents="none" opacity={0.35}>
              {Array.from({ length: Math.floor(1000 / cadDraw.grid) + 1 }, (_, i) => <line key={`v${i}`} x1={i * cadDraw.grid} y1={0} x2={i * cadDraw.grid} y2={unitH} stroke="#1a3fd1" strokeWidth={i % 4 ? 0.5 : 1} vectorEffect="non-scaling-stroke" />)}
              {Array.from({ length: Math.floor(unitH / cadDraw.grid) + 1 }, (_, i) => <line key={`h${i}`} x1={0} y1={i * cadDraw.grid} x2={1000} y2={i * cadDraw.grid} stroke="#1a3fd1" strokeWidth={i % 4 ? 0.5 : 1} vectorEffect="non-scaling-stroke" />)}
            </g>
          )}
          {/* CAD edits waiting for the sheet to redraw + entity picked for move/offset */}
          {pendingEdits.map((e) => (
            <g key={e.id} data-testid="cad-pending-edit" pointerEvents="none">
              {e.op === "add_text"
                ? <text x={e.pts[0].x} y={e.pts[0].y} fontSize={e.height || 3} fill="#1a3fd1">{e.text}</text>
                : e.op === "add_dim"
                  ? (<><line x1={e.pts[0].x} y1={e.pts[0].y} x2={e.pts[1].x} y2={e.pts[1].y} stroke="#1a3fd1" strokeWidth={2} vectorEffect="non-scaling-stroke" />{cadFpu ? <text x={(e.pts[0].x + e.pts[1].x) / 2} y={(e.pts[0].y + e.pts[1].y) / 2 - 4 / (ppu || 1)} fontSize={12 / (ppu || 1)} fill="#1a3fd1" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 / (ppu || 1) }}>{fmtDim(Math.hypot(e.pts[1].x - e.pts[0].x, e.pts[1].y - e.pts[0].y) * cadFpu, e.format)}</text> : null}</>)
                  : e.op === "add_symbol"
                    ? <circle cx={e.pts[0].x} cy={e.pts[0].y} r={(e.height || 2.5) * 1.2} fill="none" stroke="#1a3fd1" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeDasharray="3 2" />
                  : <polyline points={e.pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#1a3fd1" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeDasharray="4 3" />}
            </g>
          ))}
          {cadSel && cadSel.pts && (
            <polyline data-testid="cad-selected-entity" points={cadSel.pts.map((p) => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#f97316" strokeWidth={5} vectorEffect="non-scaling-stroke" opacity={0.8} pointerEvents="none" />
          )}
          {cadSel && cadSel.dim && previewCursor && (
            <g data-testid="cad-dim-preview" pointerEvents="none">
              <line x1={cadSel.dim.x} y1={cadSel.dim.y} x2={previewCursor.x} y2={previewCursor.y} stroke="#1a3fd1" strokeWidth={2} vectorEffect="non-scaling-stroke" strokeDasharray="5 4" />
              <circle cx={cadSel.dim.x} cy={cadSel.dim.y} r={3.5 / (ppu || 1)} fill="#1a3fd1" />
              {cadFpu && <text x={(cadSel.dim.x + previewCursor.x) / 2} y={(cadSel.dim.y + previewCursor.y) / 2 - 4 / (ppu || 1)} fontSize={12 / (ppu || 1)} fill="#1a3fd1" textAnchor="middle" style={{ paintOrder: "stroke", stroke: "#fff", strokeWidth: 3 / (ppu || 1) }}>{fmtDim(Math.hypot(constrain(previewCursor, cadSel.dim, cadDraw).x - cadSel.dim.x, constrain(previewCursor, cadSel.dim, cadDraw).y - cadSel.dim.y) * cadFpu, cadDraw?.dimFormat)}</text>}
            </g>
          )}
          {/* draft */}
          {draft && previewCursor && (
            <g>
              {draft.type === "linear" && (
                <polyline
                  points={[...draft.points, previewCursor].map((p) => `${p.x},${p.y}`).join(" ")}
                  fill="none" stroke={activeItem?.color || "#f97316"} strokeWidth={4}
                  vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="8 6" opacity={0.9}
                />
              )}
              {draft.type === "area" && draft.points.length > 1 && (
                <polygon
                  points={[...draft.points, previewCursor].map((p) => `${p.x},${p.y}`).join(" ")}
                  fill={activeItem?.color || "#f97316"} fillOpacity={0.15} stroke={activeItem?.color || "#f97316"}
                  strokeWidth={4} vectorEffect="non-scaling-stroke" strokeDasharray="8 6"
                />
              )}
              {draft.points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={3.5 / (ppu || 1)} fill="#fff" stroke={activeItem?.color || "#f97316"} strokeWidth={2 / (ppu || 1)} />
              ))}
            </g>
          )}
          {/* scale two-click preview */}
          {scaleP1 && cursor && (
            <g>
              <line x1={scaleP1.x} y1={scaleP1.y} x2={cursor.x} y2={cursor.y} stroke="#f97316" strokeWidth={3} vectorEffect="non-scaling-stroke" strokeDasharray="6 5" />
              <circle cx={scaleP1.x} cy={scaleP1.y} r={4 / (ppu || 1)} fill="#f97316" />
            </g>
          )}
          {/* CAD snap marker: square = endpoint, triangle = midpoint, circle = on line */}
          {cadSnapMark && (
            <g data-testid="cad-snap-marker" pointerEvents="none">
              {cadSnapMark.kind === "endpoint"
                ? <rect x={cadSnapMark.x - 5 / (ppu || 1)} y={cadSnapMark.y - 5 / (ppu || 1)} width={10 / (ppu || 1)} height={10 / (ppu || 1)} fill="none" stroke="#1a3fd1" strokeWidth={2} vectorEffect="non-scaling-stroke" />
                : <circle cx={cadSnapMark.x} cy={cadSnapMark.y} r={5 / (ppu || 1)} fill="none" stroke="#1a3fd1" strokeWidth={2} vectorEffect="non-scaling-stroke" />}
            </g>
          )}
          {/* drag rectangles */}
          {dragRect && (() => { const r = normRect(dragRect); return (
            <rect x={r.x} y={r.y} width={r.w} height={r.h}
              fill={dragRect.kind === "highlight" ? "rgba(250,204,21,0.25)" : "rgba(249,115,22,0.08)"}
              stroke={dragRect.kind === "highlight" ? "#eab308" : "#f97316"} strokeWidth={2}
              vectorEffect="non-scaling-stroke" strokeDasharray="6 4" />
          ); })()}
          {/* selection boundary + handles */}
          {selBounds && selection.length > 0 && (
            <g>
              <rect x={selBounds.minX - 4 / (ppu || 1)} y={selBounds.minY - 4 / (ppu || 1)}
                width={selBounds.w + 8 / (ppu || 1)} height={selBounds.h + 8 / (ppu || 1)}
                fill="none" stroke="#f97316" strokeWidth={1.5} vectorEffect="non-scaling-stroke" strokeDasharray="5 4" />
              {[[selBounds.minX, selBounds.minY], [selBounds.maxX, selBounds.minY], [selBounds.maxX, selBounds.maxY], [selBounds.minX, selBounds.maxY]].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r={4 / (ppu || 1)} fill="#fff" stroke="#f97316" strokeWidth={1.5 / (ppu || 1)} />
              ))}
            </g>
          )}
          {/* draggable vertex handles — single selected line/area only */}
          {vertexItem && !moveState && (
            <g data-testid="vertex-handles">
              {vertexItem.points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y}
                  r={(vertexDrag && vertexDrag.index === i ? 7 : 5.5) / (ppu || 1)}
                  fill={vertexDrag && vertexDrag.index === i ? "#f97316" : "#fff"}
                  stroke="#f97316" strokeWidth={2 / (ppu || 1)}
                  style={{ cursor: "grab" }}
                  data-testid={`vertex-handle-${i}`} />
              ))}
            </g>
          )}
        </svg>
      </div>

      {/* draft readout */}
      {draftReadout && (
        <div className="pointer-events-none absolute z-10 rounded-md bg-[#0b172a]/90 px-2.5 py-1 font-mono text-[11px] text-white shadow-lg"
          style={{ left: "50%", bottom: 16, transform: "translateX(-50%)" }}
          data-testid="draft-readout">
          {fpu ? <>segment {fmtFtIn(draftReadout.seg)} · total {fmtFtIn(draftReadout.total)}</> : "No scale set — set the page scale to get feet"}
          {" "}· Enter/double-click finishes · Backspace undoes
        </div>
      )}

      {/* live footage label riding beside the cursor while drawing a line —
          solid near-black, white 14 px semibold, labeled rows, never scaled
          by PDF zoom (HTML overlay), pointer-events none so it can't block
          clicks/pans/snaps, clamped inside the viewport */}
      {draft?.type === "linear" && previewCursor && cursorClient && !panState && !pendingClick && (
        <div
          className="pointer-events-none fixed z-30 select-none rounded-lg border border-white/15 bg-[#0b0b0f] px-3 py-2 shadow-xl"
          style={{
            left: Math.min(cursorClient.x + 18, (typeof window !== "undefined" ? window.innerWidth : 400) - 190),
            top: Math.max(Math.min(cursorClient.y - 64, (typeof window !== "undefined" ? window.innerHeight : 600) - 70), 4),
          }}
          data-testid="live-footage-label"
        >
          {fpu && draftReadout ? (
            <>
              <p className="whitespace-nowrap text-[14px] font-semibold leading-snug text-white" data-testid="live-footage-segment">
                Current segment <span className="font-mono">{fmtFtIn(draftReadout.seg)}</span>
              </p>
              <p className="whitespace-nowrap text-[14px] font-semibold leading-snug text-white/85" data-testid="live-footage-total">
                Total <span className="font-mono">{fmtFtIn(draftReadout.total)}</span>
              </p>
            </>
          ) : (
            <p className="whitespace-nowrap text-[14px] font-semibold text-white">No scale set</p>
          )}
        </div>
      )}

      {/* live readout while dragging a vertex — same treatment as the
          drawing footage label; Esc cancels, release commits (undoable) */}
      {vertexDrag && vertexReadout && cursorClient && (
        <div
          className="pointer-events-none fixed z-30 select-none rounded-lg border border-white/15 bg-[#0b0b0f] px-3 py-2 shadow-xl"
          style={{
            left: Math.min(cursorClient.x + 18, (typeof window !== "undefined" ? window.innerWidth : 400) - 190),
            top: Math.max(Math.min(cursorClient.y - 64, (typeof window !== "undefined" ? window.innerHeight : 600) - 70), 4),
          }}
          data-testid="vertex-drag-readout"
        >
          <p className="whitespace-nowrap text-[14px] font-semibold leading-snug text-white">
            {vertexReadout.type === "area" ? (
              <>Area <span className="font-mono">{vertexReadout.qty.toFixed(1)} sq ft</span></>
            ) : (
              <>Total <span className="font-mono">{fmtFtIn(vertexReadout.qty)}</span></>
            )}
          </p>
          <p className="whitespace-nowrap text-[11px] leading-snug text-white/60">Release to commit · Esc cancels · Ctrl+Z undoes</p>
        </div>
      )}

      {/* hover tooltip */}
      {hover && tool === "select" && (
        <div className="pointer-events-none fixed z-30 max-w-[240px] rounded-lg bg-[#0b172a]/95 px-3 py-2 text-[11px] leading-snug text-white shadow-xl"
          style={{ left: Math.min(hover.cx + 14, (typeof window !== "undefined" ? window.innerWidth : 400) - 250), top: hover.cy + 14 }}
          data-testid="takeoff-hover-tip">
          <p className="font-semibold">{hover.item.name}</p>
          <p className="font-mono text-white/80">
            {hover.item.type === "count" ? "count" : hover.item.type === "area" ? `${(hover.item.quantity || 0).toFixed(1)} sq ft` : fmtFtIn(hover.item.quantity || 0)}
            {(hover.item.multiplier || 1) > 1 ? ` ×${hover.item.multiplier}` : ""}
            {hover.item.ductSpec ? ` · ${(ductSqFt(hover.item) * (hover.item.multiplier || 1)).toFixed(1)} sq ft priced` : ""}
          </p>
          <p className="text-white/50">{pageState.scaleLabel}{(hover.item.labels || []).length ? ` · ${hover.item.labels.join(", ")}` : ""}</p>
        </div>
      )}
    </div>
  );
}

const normRect = (r) => ({
  x: r.w < 0 ? r.x + r.w : r.x,
  y: r.h < 0 ? r.y + r.h : r.y,
  w: Math.abs(r.w), h: Math.abs(r.h),
});

/** One saved takeoff on the SVG overlay — 4 px non-scaling strokes,
 * 22% area fills, count markers at a fixed ~6 px screen radius. */
const Mark = ({ it, ppu, selected, hovered }) => {
  const pts = it.points || [];
  const str = pts.map((p) => `${p.x},${p.y}`).join(" ");
  const glow = selected ? (
    <polyline points={str} fill="none" stroke="#f97316" strokeWidth={8} vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" opacity={0.45} />
  ) : null;
  if (it.type === "count") {
    const r = 6 / (ppu || 1);
    const [p] = pts;
    return (
      <g>
        {selected && <circle cx={p.x} cy={p.y} r={10 / (ppu || 1)} fill="none" stroke="#f97316" strokeWidth={2 / (ppu || 1)} />}
        <circle cx={p.x} cy={p.y} r={r} fill={it.color || "#2563eb"} stroke={hovered && !selected ? "#0b172a" : "none"} strokeWidth={1.5 / (ppu || 1)} />
        <path d={`M ${p.x - r * 0.5} ${p.y} L ${p.x + r * 0.5} ${p.y} M ${p.x} ${p.y - r * 0.5} L ${p.x} ${p.y + r * 0.5}`}
          stroke="#fff" strokeWidth={2 / (ppu || 1)} />
      </g>
    );
  }
  if (it.type === "area") {
    return (
      <g>
        {selected && <polygon points={str} fill="none" stroke="#f97316" strokeWidth={8} vectorEffect="non-scaling-stroke" opacity={0.45} strokeLinejoin="round" />}
        <polygon points={str} fill={it.color || "#2563eb"} fillOpacity={0.22} stroke={it.color || "#2563eb"}
          strokeWidth={4} vectorEffect="non-scaling-stroke" strokeLinejoin="round"
          style={hovered && !selected ? { stroke: "#0b172a" } : undefined} />
      </g>
    );
  }
  return (
    <g>
      {glow}
      <polyline points={str} fill="none" stroke={hovered && !selected ? "#0b172a" : it.color || "#2563eb"}
        strokeWidth={4} vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
};
